import React, { useContext, memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles, Select, Icon } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from "../../lib/Web5Context";
import ConditionsContext from "./ConditionsContext";
import FieldValue from "./FieldValue";
import styles from "./styles/condition";
const useStyles = createUseStyles(styles, 'ConditionsCondition');
const operators = {
  '==': 'cfm_label_equals',
  '!=': 'cfm_label_not_equals',
  '>': 'cfm_label_gth',
  '>=': 'cfm_label_gth_or_eq',
  '<': 'cfm_label_lth',
  '<=': 'cfm_label_lth_or_eq'
};
const Condition = _ref => {
  var _condition$field3, _condition$operator;
  let {
    condition,
    index,
    onChange,
    onRemove,
    options
  } = _ref;
  const {
    t
  } = useWeb5Context();
  const classes = useStyles();
  const {
    fields,
    isReadOnly
  } = useContext(ConditionsContext);
  const fieldOptions = useMemo(() => fields.map(f => {
    const {
      label,
      value,
      isExtra
    } = f;
    return {
      label: isExtra ? label : t(label),
      value
    };
  }), [fields, t]);
  const operatorOptions = useMemo(() => {
    if (!fields || !condition.field) return [];
    const foundField = fields.find(x => {
      var _condition$field;
      return x.value === ((condition === null || condition === void 0 ? void 0 : (_condition$field = condition.field) === null || _condition$field === void 0 ? void 0 : _condition$field.value) || (condition === null || condition === void 0 ? void 0 : condition.field));
    });
    if (!(foundField !== null && foundField !== void 0 && foundField.conditions)) return;
    return foundField.conditions.filter(c => operators[c] !== undefined).map(cond => ({
      label: t(operators[cond]),
      value: cond
    }));
  }, [condition, fields, t]);
  const fieldSchema = useMemo(() => {
    if (!fields || fields.length <= 0) return {};
    return fields.find(x => {
      var _condition$field2;
      return x.value === ((condition === null || condition === void 0 ? void 0 : (_condition$field2 = condition.field) === null || _condition$field2 === void 0 ? void 0 : _condition$field2.value) || (condition === null || condition === void 0 ? void 0 : condition.field));
    }) || {};
  }, [condition, fields]);
  const handleFieldChange = useCallback(val => {
    onChange(index, {
      field: val,
      operator: null,
      value: null
    });
  }, [index, onChange]);
  const handleOperatorChange = useCallback(val => {
    let newValues = {
      operator: val
    };
    if (!val) newValues.value = null;
    onChange(index, newValues);
  }, [index, onChange]);
  const handleValueChange = useCallback(e => {
    var _e$target;
    let value = (e === null || e === void 0 ? void 0 : (_e$target = e.target) === null || _e$target === void 0 ? void 0 : _e$target.value) || e;
    switch (fieldSchema === null || fieldSchema === void 0 ? void 0 : fieldSchema.type) {
      case 'currency':
      case 'int':
        value = !isNaN(value) ? parseInt(value, 10) : value;
        break;
      case 'decimal':
        value = !isNaN(value) ? parseFloat(value) : value;
        break;
      default:
        break;
    }
    onChange(index, {
      value
    });
  }, [fieldSchema, index, onChange]);
  const handleRemoveCondition = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);
  const fieldReadOnly = useMemo(() => !condition.field || isReadOnly, [condition.field, isReadOnly]);
  const valueReadOnly = useMemo(() => !(condition.field && condition.operator) || isReadOnly, [condition.field, condition.operator, isReadOnly]);
  const conditionFieldClassNames = classnames(classes.field, 'conditions__condition__select__field');
  const conditionOperatorClassNames = classnames(classes.field, 'conditions__condition__select__operator');
  const conditionValueClassNames = classnames(classes.field, 'conditions__condition__field-value', {
    'conditions__condition__field-value--disabled': valueReadOnly
  });
  const conditionDeleteClassNames = classnames(classes.trashIcon, 'conditions__condition__delete-condition-button');
  const field = condition.field && !((_condition$field3 = condition.field) !== null && _condition$field3 !== void 0 && _condition$field3.value) ? fieldOptions === null || fieldOptions === void 0 ? void 0 : fieldOptions.find(op => op.value === condition.field) : condition.field;
  const operator = condition.operator && !((_condition$operator = condition.operator) !== null && _condition$operator !== void 0 && _condition$operator.value) ? operatorOptions === null || operatorOptions === void 0 ? void 0 : operatorOptions.find(op => op.value === condition.operator) : condition.operator;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement(Select, {
    className: conditionFieldClassNames,
    value: field,
    options: fieldOptions,
    onChange: handleFieldChange,
    isReadOnly: isReadOnly,
    isFullWidth: true
  }), /*#__PURE__*/React.createElement(Select, {
    className: conditionOperatorClassNames,
    value: operator,
    options: operatorOptions,
    onChange: handleOperatorChange,
    isReadOnly: fieldReadOnly,
    isFullWidth: true
  }), /*#__PURE__*/React.createElement(FieldValue, {
    className: conditionValueClassNames,
    value: condition.value,
    onChange: handleValueChange,
    isReadOnly: valueReadOnly,
    type: fieldSchema.type || 'text',
    table: fieldSchema.table,
    options: options,
    selectedField: condition.field
  }), !isReadOnly && /*#__PURE__*/React.createElement(Icon, {
    className: conditionDeleteClassNames,
    name: "trash",
    onClick: handleRemoveCondition
  }));
};
process.env.NODE_ENV !== "production" ? Condition.propTypes = {
  condition: PropTypes.shape({
    operator: PropTypes.string,
    field: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.arrayOf(PropTypes.any), PropTypes.bool, PropTypes.object])
  }).isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
} : void 0;
export default /*#__PURE__*/memo(Condition);